import type { MeLoId, MeloCurrentPool, SmartMeterGateway, TafProfileInfo } from '../types/network';
import { groupTafProfilesByMeter, meterDirection, type MeterDirection, type MeterGroup } from './smgwUtils';

/**
 * Where the current value of one Zähler comes from:
 * - `measured`: TAF-10 live value (for a MeLo: booked on it by the backend)
 * - `stale`: last TAF-10 value, older than the live window (not counted)
 * - `estimated`: backend estimate (HouseholdCurrentPowerService: trafo-balanced or load profile)
 * - `pooled`: a measurement exists in the house, but cannot be attributed to this meter
 * - `none`: nothing current (e.g. only register profiles TAF 1/7, values on demand)
 */
export type MeterValueSource = 'measured' | 'stale' | 'estimated' | 'pooled' | 'none';

/** One SMGW meter (grouped TAF profiles) attached to a row. */
export interface MeterRowMeter {
  group: MeterGroup;
  smgw: SmartMeterGateway;
}

/**
 * One row of the "Zähler" card:
 * - `melo`: an ERP Messlokation, with the SMGW meters that belong to it
 * - `unassigned`: an SMGW meter in a house WITH MeLos that the backend could not book on one (its value is part
 *   of the house pool)
 * - `meter`: an SMGW meter in a house without MeLos
 */
export interface MeterRow {
  key: string;
  kind: 'melo' | 'unassigned' | 'meter';
  melo?: MeLoId;
  meters: MeterRowMeter[];
  direction?: MeterDirection;
  /** Current power (W, + Bezug / − Einspeisung) when known. */
  powerW?: number;
  valueSource: MeterValueSource;
  /** Instant of a measured or stale value. */
  measuredAt?: string;
}

export interface MeterSummary {
  rows: MeterRow[];
  /** Number of Zähler: the MeLos when the house has any (pool meters are among them), else the SMGW meters. */
  meterCount: number;
  measuredW: number;
  measuredCount: number;
  /** Estimated meters incl. the pool's per-meter estimates. */
  estimatedW: number;
  estimatedCount: number;
  /** Pool estimate for meters without measurement when readings could not be attributed (Fall B). */
  poolEstimate?: { perMeterW: number; count: number };
  /** Σ of all current values, null when nothing is known. */
  totalW: number | null;
  annualKwh: number;
}

const norm = (id: string | undefined): string => (id ?? '').trim().toUpperCase();

/** Fresh TAF-10 values of a meter (several only in odd registries) and their newest instant. */
const liveOf = (m: MeterRowMeter): { powerW?: number; at?: string; fresh: boolean } => {
  const values = m.group.liveProfiles.filter(p => p.powerW != null);
  if (values.length === 0) return { fresh: false };
  const at = values.map(p => p.timestamp).filter((t): t is string => !!t).sort().pop();
  return { powerW: values.reduce((s, p) => s + (p.powerW ?? 0), 0), at, fresh: !!m.smgw.hasRealtimeData };
};

const rowDirection = (meters: MeterRowMeter[]): MeterDirection | undefined =>
  meters.map(m => meterDirection(m.group)).find((d): d is MeterDirection => d != null);

/**
 * The rows of a house's "Zähler" card. Values and the booking of a TAF-10 reading
 * on a MeLo come from the backend (`HouseholdCurrentPowerService`: `melo.currentPowerW`
 * / `currentPowerSource`, `TafProfileInfo.attributedMeLoId`, `meloCurrentPool`) — this
 * function only arranges them, so a reading is never shown or summed twice.
 * Meters are attached to a MeLo row by the backend's booking or by a shared MeLo;
 * a meter with Bezug and Einspeisung MeLo appears under both rows (its value only
 * where the backend booked it).
 */
export function buildMeterRows(smgws: SmartMeterGateway[], melos: MeLoId[], pool?: MeloCurrentPool): MeterSummary {
  const meters: MeterRowMeter[] = [];
  for (const smgw of smgws) {
    for (const group of groupTafProfilesByMeter(smgw.tafProfiles ?? [])) meters.push({ group, smgw });
  }
  const annualKwh = melos.reduce((sum, m) => sum + (m.annualConsumption ?? 0), 0);

  if (melos.length === 0) {
    // House without ERP MeLos: one row per SMGW meter, the live TAF-10 value is the value.
    const rows = meters.map((m): MeterRow => {
      const live = liveOf(m);
      return {
        key: m.group.key,
        kind: 'meter',
        meters: [m],
        direction: meterDirection(m.group),
        powerW: live.powerW,
        valueSource: live.powerW == null ? 'none' : live.fresh ? 'measured' : 'stale',
        measuredAt: live.at,
      };
    });
    const measured = rows.filter(r => r.valueSource === 'measured');
    const measuredW = measured.reduce((s, r) => s + (r.powerW ?? 0), 0);
    return {
      rows,
      meterCount: rows.length,
      measuredW,
      measuredCount: measured.length,
      estimatedW: 0,
      estimatedCount: 0,
      totalW: measured.length > 0 ? measuredW : null,
      annualKwh,
    };
  }

  // House with MeLos: one row per MeLo, attached meters by the backend's booking or a shared MeLo.
  const attached = new Set<MeterRowMeter>();
  const bookedOn = (p: TafProfileInfo, melo: MeLoId) => norm(p.attributedMeLoId) === norm(melo.id);
  const rows: MeterRow[] = melos.map(melo => {
    const own = meters.filter(m =>
      m.group.profiles.some(p => bookedOn(p, melo)) || m.group.meLoIds.some(id => norm(id) === norm(melo.id)));
    own.forEach(m => attached.add(m));
    const booked = own.flatMap(m => m.group.liveProfiles.filter(p => bookedOn(p, melo)));
    return {
      key: `melo:${melo.id}`,
      kind: 'melo',
      melo,
      meters: own,
      direction: rowDirection(own),
      powerW: melo.currentPowerW,
      valueSource: melo.currentPowerSource === 'measured' ? 'measured'
        : melo.currentPowerSource === 'estimated' ? 'estimated'
          : melo.currentPowerSource === 'pooled' ? 'pooled'
            : 'none',
      measuredAt: booked.map(p => p.timestamp).filter((t): t is string => !!t).sort().pop(),
    };
  });

  // One MeLo without meter and one meter without MeLo: the same Zähler by structure (also while its TAF-10 is stale
  // or before the backend booked it) — never the house-pool case with several open MeLos.
  const openMeters = meters.filter(m => !attached.has(m));
  const openRows = rows.filter(r => r.meters.length === 0);
  if (openMeters.length === 1 && openRows.length === 1 && !(pool && pool.measuredCount > 0)) {
    openRows[0].meters = [openMeters[0]];
    openRows[0].direction = rowDirection(openRows[0].meters);
    attached.add(openMeters[0]);
  }

  // Meters the backend could not book on a MeLo: their fresh values are the house pool's measured part.
  for (const m of meters.filter(x => !attached.has(x))) {
    const live = liveOf(m);
    const inPool = live.fresh && live.powerW != null && !!pool && pool.measuredCount > 0;
    rows.push({
      key: m.group.key,
      kind: 'unassigned',
      meters: [m],
      direction: meterDirection(m.group),
      powerW: live.powerW,
      valueSource: live.powerW == null ? 'none' : inPool ? 'measured' : 'stale',
      measuredAt: live.at,
    });
  }

  // Totals exactly as the backend booked them: MeLo values + pool
  let measuredW = 0, measuredCount = 0, estimatedW = 0, estimatedCount = 0;
  for (const r of rows.filter(x => x.kind === 'melo')) {
    if (r.valueSource === 'measured') { measuredW += r.powerW ?? 0; measuredCount++; }
    if (r.valueSource === 'estimated') { estimatedW += r.powerW ?? 0; estimatedCount++; }
  }
  if (pool && pool.measuredCount > 0) {
    measuredW += pool.measuredPowerW ?? 0;
    measuredCount += pool.measuredCount;
  }
  let poolEstimate: MeterSummary['poolEstimate'];
  if (pool && pool.estimatedPerMeterW != null && pool.estimatedCount > 0) {
    poolEstimate = { perMeterW: pool.estimatedPerMeterW, count: pool.estimatedCount };
    estimatedW += pool.estimatedPerMeterW * pool.estimatedCount;
    estimatedCount += pool.estimatedCount;
  }

  return {
    rows,
    meterCount: melos.length,
    measuredW,
    measuredCount,
    estimatedW,
    estimatedCount,
    poolEstimate,
    totalW: measuredCount + estimatedCount > 0 ? measuredW + estimatedW : null,
    annualKwh,
  };
}
